import type { ButtonProps } from '../buttons/buttons';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePrevious } from '../../hooks/use-previous';
import { CheckIcon, PlusIcon } from '../../icons';
import { GhostButton, PrimaryButton } from '../buttons';
import { Spinner } from '../spinner';

export type SaveButtonProps = {
  saving?: boolean;
  savingFailed?: boolean;
  timeout?: number;
  saveLabel?: string;
  savingLabel?: string;
  savedLabel?: string;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonProps;

/**
 * @param {boolean} [saving] - Parameter to indicate if the data is saved currently.
 * @default undefined
 * @param {number} [timeout] - Time after which the button changes back from 'saved' to 'save' state again in ms.
 * @default 2000
 * @param {string} [saveLabel] - Optional default label for the button.
 * @default "Save"
 * @param {string} [savingLabel] - Optional label for the button when saving=true.
 * @default "Saving"
 * @param {string} [savedLabel] - Optional label for the button when saving changed from true to false. The label is shown for `timeout` ms.
 * @default "Saved"
 * @param {boolean} [savingFailed] - Optional parameter to indicate that the saving failed. In this case the 'save' label will be shown again.
 * @default undefined
 */
export function SaveButton({
  saving,
  savingFailed,
  timeout = 2000,
  saveLabel,
  savingLabel,
  savedLabel,
  ...props
}: SaveButtonProps) {
  const { t } = useTranslation();

  const [savingState, setSavingState] = useState<'save' | 'saving' | 'saved'>(saving === true ? 'saving' : 'save');

  const prevSaving = usePrevious(saving);
  const timerLabelChange = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (prevSaving && !saving) {
      setSavingState('saved');

      timerLabelChange.current = setTimeout(() => {
        setSavingState('save');
      }, timeout);
    }

    if (saving) {
      if (!prevSaving && timerLabelChange.current) {
        clearTimeout(timerLabelChange.current);
      }

      setSavingState('saving');
    }

    if (savingFailed) {
      setSavingState('save');
    }
  }, [saving, savingFailed, saveLabel, savingLabel, prevSaving, savedLabel, timeout]);

  useEffect(() => {
    return () => {
      if (timerLabelChange.current) {
        clearTimeout(timerLabelChange.current);
      }
    };
  }, []);

  const getButtonLabel = () => {
    if (savingState === 'saved') {
      return savedLabel || t('saveButton.saved');
    }

    if (savingState === 'saving') {
      return savingLabel || t('saveButton.saving');
    }

    return saveLabel || t('saveButton.save');
  };

  return savingState === 'save' ? (
    <PrimaryButton {...props}>
      {getButtonLabel()}
      <PlusIcon />
    </PrimaryButton>
  ) : (
    <GhostButton {...props} style={saving ? { pointerEvents: 'none' } : {}}>
      {getButtonLabel()}
      {savingState === 'saving' ? <Spinner /> : <CheckIcon />}
    </GhostButton>
  );
}
